import { sidebarAccessConfig } from "../../../Layouts/Sidebar/sidebarAccess";
import { isManager, isSeller } from "../../../lib/utility";
import {
  canAccessAbac,
  canAccessAbacAny,
  canViewPlatformDocumentsUi,
  getAbacValueAtPath,
  hasAnyTruthyPermission,
  shouldEnforceAbacForUser,
} from "./abacAccess";


export const ENFORCE_SMSC_ABAC = true;

const SMSC_SECTION_ALIASES = {
  list: "smsc",
  smsc: "smsc",
  http: "http_smsc",
  http_smsc: "http_smsc",
  special_settings: "special_setting",
  special_setting: "special_setting",
  groups: "group",
  group: "group",
  looping: "looping",
  blocking: "blocking",
  schedule: "schedule",
  alert: "alert",
  limits: "limits",
  hlr: "hlr",
  error_code: "error_code",
};

const SMSC_MENU_SECTIONS = [
  "smsc",
  "looping",
  "blocking",
  "special_setting",
  "schedule",
  "group",
];

function isObj(v) {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

function getSmscProvidersNode(permissions) {
  const modern = getAbacValueAtPath(permissions, ["sms", "smsc_providers"]);
  if (modern !== undefined) return modern;
  const legacy = getAbacValueAtPath(permissions, ["smsc"]);
  return legacy !== undefined ? legacy : undefined;
}

function isOn(node) {
  if (node === true || node === 1 || node === "true") return true;
  if (isObj(node)) {
    if (typeof node.view === "boolean") return node.view;
    if (typeof node.list === "boolean") return node.list;
    return hasAnyTruthyPermission(node);
  }
  return false;
}

function resolveSmscAccess(permissions, path) {
  if (!Array.isArray(path)) return null;
  const isModern = path[0] === "sms" && path[1] === "smsc_providers";
  const isLegacy = path[0] === "smsc";
  if (!isModern && !isLegacy) return null;

  if (!ENFORCE_SMSC_ABAC) return true;

  const providers = getSmscProvidersNode(permissions);
  // Missing providers with ABAC on → deny (do not keep /smsc/* open when all toggles off).
  if (providers === undefined) return false;
  if (typeof providers === "boolean") return providers;
  if (!isObj(providers)) return false;

  const sub = isModern ? path[2] : path[1];
  if (!sub) return SMSC_MENU_SECTIONS.some((key) => isOn(providers[key]));

  const key = SMSC_SECTION_ALIASES[sub] || sub;
  if (!Object.prototype.hasOwnProperty.call(providers, key)) {
    return false;
  }
  return isOn(providers[key]);
}

function resolveVendorAccess(permissions, path) {
  if (!Array.isArray(path) || path[0] !== "vendor") return null;
  const vendor = getAbacValueAtPath(permissions, ["vendor"]);
  if (vendor === undefined) return true;
  if (typeof vendor === "boolean") return vendor;
  if (!isObj(vendor)) return true;

  const leaf = path[1];
  if (!leaf || leaf === "view" || leaf === "list") {
    if (typeof vendor.view === "boolean") return vendor.view;
    return hasAnyTruthyPermission(vendor);
  }
  if (typeof vendor.view === "boolean" && vendor.view === false) return false;
  return vendor[leaf] === true;
}


const NON_PAGE_KEYS = new Set([
  "common",
  "default",
  "navbar",
  "wallet",
  "role",
  "administration",
  "other",
]);

function hasAnyPageToggleOn(permissions) {
  if (!isObj(permissions)) return false;
  return Object.entries(permissions).some(([key, value]) => {
    if (NON_PAGE_KEYS.has(key) || String(key).startsWith("_")) return false;
    // Unflattened API shape: page modules live under channels.*.
    if (key === "channels" && isObj(value)) {
      return Object.values(value).some((channel) =>
        hasAnyTruthyPermission(channel)
      );
    }
    return hasAnyTruthyPermission(value);
  });
}

function hasAnyEnabledAbacLeaf(permissions) {
  if (!isObj(permissions)) return false;
  return Object.entries(permissions).some(([key, value]) => {
    if (key === "role" || String(key).startsWith("_")) return false;
    return hasAnyTruthyPermission(value);
  });
}

export const SMS_MESSAGE_VIEW_PATHS = [
  ["sms", "message", "quick_send", "view"],
  ["sms", "message", "bulk_campaign", "view"],
  ["sms", "message", "personalized", "view"],
  ["sms", "message", "auto_schedule", "view"],
  ["sms", "message", "messages", "view"],
];

function resolveErrorCodeAccess(permissions, path) {
  if (!Array.isArray(path) || path[0] !== "error_code") {
    return false;
  }
  const sub = path[1];

  const legacy = getAbacValueAtPath(permissions, ["error_code", sub]);

  if (sub === "smsc") {
    const providers = getAbacValueAtPath(permissions, ["sms", "smsc_providers"]);
    if (isObj(providers)) {
      return Object.prototype.hasOwnProperty.call(providers, "error_code")
        ? isOn(providers.error_code)
        : false;
    }
    if (legacy !== undefined) return isOn(legacy);
    return false;
  }

  if (legacy !== undefined) return isOn(legacy);
  return false;
}

function isErrorCodePath(pathname, mapped) {
  const path = String(pathname || "");
  if (/^\/(user|smsc)\/error-code/i.test(path)) return true;
  return (mapped || []).some((segments) => segments?.[0] === "error_code");
}

function getRoutePermissionValue(permissions, path) {
  const value = getAbacValueAtPath(permissions, path);
  if (value !== undefined) return value;
  const leaf = path[path.length - 1];
  const parent = path.slice(0, -1);
  // Legacy leaf aliases from older catalogues / Omni leftovers.
  if (leaf === "BasicUpdate" || leaf === "BasicView") {
    return (
      getAbacValueAtPath(permissions, [...parent, "BasicView"]) ??
      getAbacValueAtPath(permissions, [...parent, "BasicUpdate"]) ??
      getAbacValueAtPath(permissions, [...parent, "basic"])
    );
  }
  if (leaf === "OptionalView" || leaf === "OptionalUpdate") {
    return (
      getAbacValueAtPath(permissions, [...parent, "OptionalView"]) ??
      getAbacValueAtPath(permissions, [...parent, "OptionalUpdate"]) ??
      getAbacValueAtPath(permissions, [...parent, "optional"])
    );
  }
  if (leaf === "MMsView" || leaf === "MmsUpdate") {
    return (
      getAbacValueAtPath(permissions, [...parent, "MMsView"]) ??
      getAbacValueAtPath(permissions, [...parent, "MmsUpdate"]) ??
      getAbacValueAtPath(permissions, [...parent, "mms"])
    );
  }
  return undefined;
}

const ABAC_LANDING_SKIP = new Set([
  "/user/abac",
  "/user/multi-access",
  "/update-password",
]);

function collectAbacLandingPaths(user) {
  const { ALWAYS_ALLOWED, SELLER_MANAGER_RULES, STAFF_RULES } =
    sidebarAccessConfig;
  const always = ALWAYS_ALLOWED.filter((path) => !ABAC_LANDING_SKIP.has(path));
  const rules =
    isSeller(user) || isManager(user) ? SELLER_MANAGER_RULES : STAFF_RULES;
  return [
    ...new Set([
      "/dashboard",
      ...rules.map((rule) => rule.path),
      ...always,
    ]),
  ];
}


function isLandingPathAllowed(pathname, permissions, user, profile) {
  if (
    /^\/developer\/sdk/i.test(String(pathname || "")) ||
    /^\/api-runner/i.test(String(pathname || ""))
  ) {
    return canAccessAbac(
      permissions,
      ["platform_documents", "cpaas_business_api"],
      user,
      profile
    );
  }
  if (/^\/downloads/i.test(String(pathname || ""))) {
    return canViewPlatformDocumentsUi(permissions, user, profile);
  }
  const mapped = resolveAbacPathsForLocation(pathname);
  if (!mapped?.length) return false;
  if (isErrorCodePath(pathname, mapped)) {
    return mapped.some((path) => resolveErrorCodeAccess(permissions, path));
  }
  // SMSC landings must use the same gate as isAbacRouteAllowed (no fail-open).
  const smscGates = mapped.map((path) => resolveSmscAccess(permissions, path));
  if (smscGates.every((result) => result !== null)) {
    return smscGates.some(Boolean);
  }
  const present = mapped.filter(
    (path) => getRoutePermissionValue(permissions, path) !== undefined
  );
  if (!present.length) return false;

  return present.length === 1
    ? canAccessAbac(permissions, present[0], user, profile)
    : canAccessAbacAny(permissions, present, user, profile);
}


export const ABAC_ROUTE_RULES = [
  { match: /^\/dashboard/i, path: ["sms", "dashboard"] },
  { match: /^\/message/i, paths: SMS_MESSAGE_VIEW_PATHS },
  { match: /^\/template$/i, path: ["sms", "template"] },

  { match: /^\/user\/abac/i, path: ["user", "multi_access"] },
  { match: /^\/user\/multi-access/i, path: ["user", "multi_access"] },
  { match: /^\/user\/limit/i, path: ["user", "limit"] },
  { match: /^\/user\/error-code/i, path: ["error_code", "user"] },
  { match: /^\/user/i, path: ["user", "profile"] },

  { match: /^\/sales/i, path: ["sales_team"] },
  { match: /^\/company/i, path: ["company"] },
  { match: /^\/vendor\/smsc/i, path: ["vendor", "view_smsc"] },
  { match: /^\/vendor/i, path: ["vendor"] },
  {
    match: /^\/address-book\/[^/]+/i,
    path: ["address_book", "member", "view"],
  },
  { match: /^\/address-book/i, path: ["address_book", "view"] },
  { match: /^\/profile/i, path: ["navbar", "profile"] },
  { match: /^\/wallet/i, path: ["navbar", "wallet"] },
  {
    match: /^\/downloads/i,
    // DU: Downloads / Documentation gated by Features Documents only.
    paths: [
      ["platform_documents", "cpaas_documentation", "features_documents"],
    ],
  },
  {
    match: /^\/developer\/sdk/i,
    path: ["platform_documents", "cpaas_business_api"],
  },
  {
    match: /^\/api-runner/i,
    path: ["platform_documents", "cpaas_business_api"],
  },
  { match: /^\/network/i, path: ["network"] },
  { match: /^\/firewall/i, path: ["sms", "firewall"] },

  { match: /^\/dlt\/principle/i, path: ["dlt", "principle"] },
  { match: /^\/dlt\/template/i, path: ["dlt", "template"] },
  { match: /^\/dlt/i, path: ["dlt"] },

  { match: /^\/smsc\/error-code/i, path: ["error_code", "smsc"] },
  { match: /^\/smsc\/list/i, path: ["sms", "smsc_providers", "smsc"] },
  { match: /^\/smsc\/http/i, path: ["sms", "smsc_providers", "http_smsc"] },
  { match: /^\/smsc\/alert/i, path: ["sms", "smsc_providers", "alert"] },
  { match: /^\/smsc\/limits/i, path: ["sms", "smsc_providers", "limits"] },
  { match: /^\/smsc\/looping/i, path: ["sms", "smsc_providers", "looping"] },
  { match: /^\/smsc\/hlr/i, path: ["sms", "smsc_providers", "hlr"] },
  { match: /^\/smsc\/blocking/i, path: ["sms", "smsc_providers", "blocking"] },
  { match: /^\/smsc\/schedule/i, path: ["sms", "smsc_providers", "schedule"] },
  { match: /^\/smsc\/special-settings/i, path: ["sms", "smsc_providers", "special_setting"] },
  { match: /^\/smsc\/group-data/i, path: ["sms", "smsc_providers", "group"] },
  { match: /^\/smsc/i, path: ["sms", "smsc_providers"] },

  { match: /^\/reports\/campaign\/aborted/i, path: ["sms", "reports", "campaign", "aborted"] },
  { match: /^\/reports\/campaign\/campaign/i, path: ["sms", "reports", "campaign", "campaign"] },
  { match: /^\/reports\/campaign\/schedule/i, path: ["sms", "reports", "campaign", "schedule"] },
  { match: /^\/reports\/summary\/delivery/i, path: ["sms", "reports", "summary", "delivery"] },
  { match: /^\/reports\/summary\/batch/i, path: ["sms", "reports", "summary", "batch"] },
  { match: /^\/reports\/detailed\/advanced/i, path: ["sms", "reports", "detailed_analytics", "delivery_advance"] },
  { match: /^\/reports\/detailed\/content/i, path: ["sms", "reports", "detailed_analytics", "delivery_content"] },
  { match: /^\/reports\/detailed\/smsc/i, path: ["sms", "reports", "detailed_analytics", "delivery_smsc"] },
  { match: /^\/reports\/latency/i, path: ["sms", "reports", "delivery_latency"] },
  { match: /^\/reports\/consumption/i, path: ["sms", "reports", "consumption"] },
  { match: /^\/reports\/lookup/i, path: ["sms", "reports", "lookup"] },
  { match: /^\/reports\/aggregated/i, path: ["sms", "reports", "aggregator"] },
  { match: /^\/reports\/profit/i, path: ["sms", "reports", "profit"] },
  { match: /^\/reports\/performance\/user/i, path: ["sms", "reports", "performance", "user"] },
  { match: /^\/reports\/performance\/route/i, path: ["sms", "reports", "performance", "route"] },
  { match: /^\/reports\/blocked/i, path: ["sms", "reports", "blocked"] },
  { match: /^\/reports\/tps/i, path: ["sms", "reports", "tps"] },
  { match: /^\/reports\/vendor-summary/i, path: ["sms", "reports", "vendor_summary"] },
  { match: /^\/reports\/edr/i, path: ["sms", "reports", "edr"] },
  { match: /^\/reports\/coverage-report/i, path: ["sms", "reports", "coverage"] },
  { match: /^\/reports\/coverage/i, path: ["sms", "reports", "coverage"] },
  { match: /^\/reports\/batch-summary/i, path: ["sms", "reports", "summary", "batch"] },
  { match: /^\/reports\/delivery-advance/i, path: ["sms", "reports", "detailed_analytics", "delivery_advance"] },
  { match: /^\/reports\/coverage$/i, path: ["sms", "reports", "coverage"] },
  { match: /^\/reports\/performance-user/i, path: ["sms", "reports", "performance", "user"] },

  { match: /^\/utilities\/edit\/sms-bulk/i, path: ["sms", "sms_utilities", "campaign_manager"] },
  { match: /^\/utilities\/edit\/bulk/i, path: ["sms", "sms_utilities", "campaign_manager"] },
  { match: /^\/utilities\/edit\/schedule/i, path: ["sms", "sms_utilities", "schedule_manager"] },
  { match: /^\/view\/bulk/i, path: ["sms", "sms_utilities", "campaign_manager"] },
  { match: /^\/view\/schedule/i, path: ["sms", "sms_utilities", "schedule_manager"] },
  {
    match: /^\/utilities\/two-way-report/i,
    path: ["sms", "sms_utilities", "two_way", "report"],
  },
  { match: /^\/utilities\/two-way/i, path: ["sms", "sms_utilities", "two_way"] },
  {
    match: /^\/approvals\/bulk\/approved-reject/i,
    path: ["sms", "sms_utilities", "approvals", "bulk", "approve_reject"],
  },
  {
    match: /^\/approvals\/bulk\/log/i,
    path: ["sms", "sms_utilities", "approvals", "bulk", "log"],
  },
  {
    match: /^\/approvals\/bulk\/?$/i,
    paths: [
      ["sms", "sms_utilities", "approvals", "bulk", "approve_reject"],
      ["sms", "sms_utilities", "approvals", "bulk", "log"],
    ],
  },
  {
    match: /^\/approvals\/sender\/approved-reject/i,
    path: ["sms", "sms_utilities", "approvals", "sender", "approve_reject"],
  },
  {
    match: /^\/approvals\/sender\/log/i,
    path: ["sms", "sms_utilities", "approvals", "sender", "log"],
  },
  {
    match: /^\/approvals\/sender\/?$/i,
    paths: [
      ["sms", "sms_utilities", "approvals", "sender", "approve_reject"],
      ["sms", "sms_utilities", "approvals", "sender", "log"],
    ],
  },

  { match: /^\/utilities\/pcaps\/list/i, path: ["sms", "sms_utilities", "pcap", "download"] },
  { match: /^\/utilities\/pcaps/i, path: ["sms", "sms_utilities", "pcap"] },
  // DU: converter has no backend ABAC leaf — gate via sibling utilities access.
  {
    match: /^\/utilities\/converter/i,
    paths: [
      ["sms", "sms_utilities", "campaign_manager"],
      ["sms", "sms_utilities", "schedule_manager"],
      ["sms", "sms_utilities", "two_way"],
      ["sms", "sms_utilities", "subscription"],
    ],
  },
  { match: /^\/utilities\/subscription/i, path: ["sms", "subscription"] },
  { match: /^\/websmpp\/subscription/i, path: ["sms", "subscription"] },
  { match: /^\/routing\/sender\/add/i, path: ["sms", "routing", "sender", "add"] },
  { match: /^\/routing\/sender\/edit/i, path: ["sms", "routing", "sender", "edit"] },
  { match: /^\/routing\/edit/i, path: ["sms", "routing", "edit"] },
  {
    match: /^\/routing\/copy/i,
    paths: [
      ["sms", "routing", "copy", "view"],
      ["sms", "routing", "copy", "copy"],
    ],
  },
  { match: /^\/routing\/add/i, path: ["sms", "routing", "add"] },
  {
    match: /^\/routing/i,
    paths: [
      ["sms", "routing", "add", "view"],
      ["sms", "routing", "add", "create"],
      ["sms", "routing", "edit"],
      ["sms", "routing", "edit", "BasicView"],
      ["sms", "routing", "edit", "BasicUpdate"],
      ["sms", "routing", "edit", "OptionalView"],
      ["sms", "routing", "edit", "OptionalUpdate"],
      ["sms", "routing", "edit", "MMsView"],
      ["sms", "routing", "edit", "MmsUpdate"],
      ["sms", "routing", "edit", "export"],
      ["sms", "routing", "copy", "view"],
      ["sms", "routing", "copy", "copy"],
      ["sms", "routing", "sender", "add", "view"],
      ["sms", "routing", "sender", "edit", "view"],
    ],
  },
];

const CHANNEL_KEYS = [
  "sms",
  "whatsapp",
  "telegram",
  "email",
  "instagram",
  "messenger",
  "rcs",
  "push",
];

export function resolveAbacPathsForLocation(pathname) {
  const path = String(pathname || "");
  for (const rule of ABAC_ROUTE_RULES) {
    if (!rule.match.test(path)) continue;
    if (rule.paths) return rule.paths;
    if (rule.path) return [rule.path];
    if (rule.anyOf) {
      return CHANNEL_KEYS.map((ch) => [ch, rule.anyOf]);
    }
    if (rule.anyOfNavbar) {
      return rule.anyOfNavbar.map((key) => ["navbar", key]);
    }
  }
  return null;
}


export function isAbacRouteAllowed(
  pathname,
  permissions,
  user,
  profile,
  explicitPath
) {
  if (!shouldEnforceAbacForUser(user, profile) || permissions == null) {
    return true;
  }
  const explicit = !explicitPath
    ? null
    : Array.isArray(explicitPath[0])
      ? explicitPath
      : [
        Array.isArray(explicitPath)
          ? explicitPath
          : String(explicitPath).split("."),
      ];
  const mapped = explicit || resolveAbacPathsForLocation(pathname);
  if (!mapped?.length) return true;

  const special = mapped.map(
    (path) => resolveVendorAccess(permissions, path) ?? resolveSmscAccess(permissions, path)
  );
  if (special.every((r) => r !== null && r !== undefined)) {
    return special.some(Boolean);
  }


  if (isErrorCodePath(pathname, mapped)) {
    return mapped.some((path) => resolveErrorCodeAccess(permissions, path));
  }

  const isSdkDocsPath =
    /^\/developer\/sdk/i.test(String(pathname || "")) ||
    /^\/api-runner/i.test(String(pathname || ""));
  const isDocumentsPath =
    !isSdkDocsPath &&
    (/^\/downloads/i.test(String(pathname || "")) ||
      (mapped || []).some(
        (segments) => segments?.[0] === "platform_documents"
      ));
  if (isDocumentsPath) {
    return canViewPlatformDocumentsUi(permissions, user, profile);
  }


  const present = mapped.filter(
    (path) => getRoutePermissionValue(permissions, path) !== undefined
  );
  const toCheck = present.length ? present : mapped;

  return toCheck.length === 1
    ? canAccessAbac(permissions, toCheck[0], user, profile)
    : canAccessAbacAny(permissions, toCheck, user, profile);
}


/** First allowed landing route when dashboard (or the current page) is denied (DU). */
export const ABAC_FALLBACK_ROUTES = [
  "/dashboard",
  "/message",
  "/template",
  "/address-book",
  "/utilities/edit/bulk",
  "/utilities/edit/schedule",
  "/utilities/two-way",
  "/utilities/converter",
  "/utilities/subscription",
  "/reports/campaign/campaign",
  "/reports/summary/delivery",
  "/approvals/bulk",
  "/user",
  "/user/error-code",
  "/routing/edit",
  "/company",
  "/vendor",
  "/firewall",
  "/sales",
  "/downloads",
  "/developer/sdk",
];

export const DASHBOARD_FALLBACK_PATHS = ["/dashboard"];

/**
 * DU: walk a fixed fallback list with isAbacRouteAllowed.
 * Returns null when every toggle is off → No Access page.
 */
export function findFirstAllowedAbacRoute(
  permissions,
  user,
  profile,
  { exclude = [] } = {}
) {
  if (!shouldEnforceAbacForUser(user, profile)) {
    return "/dashboard";
  }
  // Settled MA with no tree → No Access (do not invent /dashboard).
  if (permissions == null) {
    return null;
  }
  // All page modules off (navbar/default leftovers don't count) → No Access.
  if (!hasAnyPageToggleOn(permissions)) {
    return null;
  }
  if (!hasAnyEnabledAbacLeaf(permissions)) {
    return null;
  }
  const skip = new Set(
    (Array.isArray(exclude) ? exclude : [exclude])
      .filter(Boolean)
      .map((path) => String(path).split("?")[0])
  );
  // Use strict landing checks (present leaves only) — never fail-open to a
  // phantom route while the sidebar is empty.
  for (const route of ABAC_FALLBACK_ROUTES) {
    if (skip.has(route)) continue;
    if (isLandingPathAllowed(route, permissions, user, profile)) return route;
  }
  for (const path of collectAbacLandingPaths(user)) {
    if (skip.has(path)) continue;
    if (ABAC_FALLBACK_ROUTES.includes(path)) continue;
    if (isLandingPathAllowed(path, permissions, user, profile)) return path;
  }
  return null;
}

export function findFirstAllowedDashboardPath(permissions, user, profile) {
  return findFirstAllowedAbacRoute(permissions, user, profile);
}

export function hasNoAllowedAbacPages(permissions, user, profile) {
  if (!shouldEnforceAbacForUser(user, profile)) return false;
  // Caller must wait for ABAC settle; null after load means no pages.
  if (permissions == null) return true;
  // All page modules off → No Access even if navbar leftovers remain.
  if (!hasAnyPageToggleOn(permissions)) return true;
  if (!hasAnyEnabledAbacLeaf(permissions)) return true;
  return findFirstAllowedAbacRoute(permissions, user, profile) == null;
}
